import React, { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Modal, Spin, message } from 'antd';
import {
    useGetCompanyConfig,
    useGetProductVariantField,
    useUpdateProductVariantField,
} from '../../hooks/useCompanies';
import useLogout from '../../hooks/useLogout';

const ProductVariantFieldModal = ({ open, companyId, companyName, onClose }) => {
    const { logout } = useLogout();
    const [fieldName, setFieldName] = useState('');
    const [companyConfigData, setCompanyConfigData] = useState(null);

    const onFetchSuccess = response => {
        setFieldName(response?.data || '');
    };

    const onFetchError = error => {
        if (error?.response?.status === 401) {
            logout();
            return;
        }
        message.error(error?.response?.data?.message || 'Failed to load product variant field');
    };

    const { isLoading, refetch } = useGetProductVariantField(
        onFetchSuccess,
        onFetchError,
        companyId,
        open
    );

    const onConfigSuccess = data => {
        setCompanyConfigData(data);
    };
    const { isLoading: isConfigLoading } = useGetCompanyConfig(
        onConfigSuccess,
        companyId,
        open && !!companyId
    );

    useEffect(() => {
        if (open && companyId) {
            refetch();
        }
    }, [open, companyId, refetch]);

    useEffect(() => {
        if (!open) {
            setFieldName('');
            setCompanyConfigData(null);
        }
    }, [open]);

    const onSaveSuccess = () => {
        message.success('Product variant field saved successfully');
        onClose();
    };

    const onSaveError = error => {
        if (error?.response?.status === 401) {
            logout();
            return;
        }
        message.error(error?.response?.data?.message || 'Failed to save product variant field');
    };

    const { mutate: updateField, isLoading: isSaving } = useUpdateProductVariantField(
        onSaveSuccess,
        onSaveError
    );

    const columnOptions = useMemo(() => {
        if (!companyConfigData?.columnHeaders || !Array.isArray(companyConfigData.columnHeaders)) {
            return [];
        }
        const allColumnNames = companyConfigData.columnHeaders
            .flatMap(header => header.name || [])
            .filter(name => name && name.trim() !== '');
        return Array.from(new Set(allColumnNames)).map(name => ({ value: name }));
    }, [companyConfigData]);

    const handleSave = () => {
        updateField({
            companyId,
            productVariantFieldName: fieldName.trim() || null,
        });
    };

    return (
        <Modal
            title={`Product Variant Field${companyName ? ` - ${companyName}` : ''}`}
            open={open}
            onCancel={onClose}
            onOk={handleSave}
            okText="Save"
            cancelText="Cancel"
            confirmLoading={isSaving}
            destroyOnClose
            width={520}
        >
            <Spin spinning={isLoading || isConfigLoading}>
                <p style={{ marginBottom: 8 }}>
                    CSV/tag field name used to group SKUs with the same product variant on the
                    guideline page.
                </p>
                <AutoComplete
                    style={{ width: '100%' }}
                    options={columnOptions}
                    value={fieldName}
                    onChange={setFieldName}
                    placeholder="e.g. _Product Variant"
                    filterOption={(inputValue, option) =>
                        option.value.toUpperCase().includes(inputValue.toUpperCase())
                    }
                    allowClear
                />
            </Spin>
        </Modal>
    );
};

export default ProductVariantFieldModal;
